import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  Spinner,
  InputGroup,
} from 'react-bootstrap';
import { FaUsers, FaPlus, FaEdit, FaSearch } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import ingresosCorporativoApi from '../../services/api/ingresosCorporativoApi';
import 'react-toastify/dist/ReactToastify.css';

const emptyForm = {
  razon_social: '',
  numero_documento: '',
  email: '',
  telefono: '',
  direccion: '',
  activo: true,
};

const ClientesFacturacion = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ingresosCorporativoApi.getClientesFacturacion(false);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const filtered = items.filter((r) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.razon_social || '').toLowerCase().includes(q) ||
      (r.numero_documento || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q)
    );
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const openNew = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      razon_social: row.razon_social || '',
      numero_documento: row.numero_documento || '',
      email: row.email || '',
      telefono: row.telefono || '',
      direccion: row.direccion || '',
      activo: row.activo !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rs = formData.razon_social.trim();
    if (!rs) {
      toast.error('La razón social es obligatoria');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        razon_social: rs,
        numero_documento: formData.numero_documento?.trim() || null,
        email: formData.email?.trim() || null,
        telefono: formData.telefono?.trim() || null,
        direccion: formData.direccion?.trim() || null,
        activo: !!formData.activo,
      };
      if (editing) {
        await ingresosCorporativoApi.updateClienteFacturacion(editing.id, payload);
        toast.success('Cliente actualizado');
      } else {
        await ingresosCorporativoApi.createClienteFacturacion(payload);
        toast.success('Cliente registrado');
      }
      setShowModal(false);
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `¿Dar de baja al cliente "${row.razon_social}"? No se puede si tiene facturas asociadas.`
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await ingresosCorporativoApi.deleteClienteFacturacion(row.id);
      toast.success('Cliente dado de baja');
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <ToastContainer />
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FaUsers className="me-2 text-primary" />
            Clientes (facturación / ingresos)
          </h4>
          <small className="text-muted">
            Clientes para registrar facturas de venta y asociarlas a centros de costo.
          </small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={openNew}>
            <FaPlus className="me-1" /> Nuevo
          </Button>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Buscar por razón social, documento o email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading && !items.length ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Razón social</th>
                    <th>Documento</th>
                    <th>Contacto</th>
                    <th>Estado</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id}>
                      <td className="fw-medium">{row.razon_social}</td>
                      <td>{row.numero_documento || '—'}</td>
                      <td>
                        <div className="small">
                          {row.email && <div>{row.email}</div>}
                          {row.telefono && <div>{row.telefono}</div>}
                          {!row.email && !row.telefono && '—'}
                        </div>
                      </td>
                      <td>
                        {row.activo ? (
                          <Badge bg="success">Activo</Badge>
                        ) : (
                          <Badge bg="secondary">Inactivo</Badge>
                        )}
                      </td>
                      <td className="text-nowrap">
                        <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(row)}>
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                          disabled={!row.activo}
                        >
                          Baja
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center text-muted py-4">No hay registros.</div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar cliente' : 'Nuevo cliente'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Label>Razón social *</Form.Label>
                <Form.Control
                  name="razon_social"
                  value={formData.razon_social}
                  onChange={handleInputChange}
                  required
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Documento (RUC / DNI)</Form.Label>
                <Form.Control
                  name="numero_documento"
                  value={formData.numero_documento}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Teléfono</Form.Label>
                <Form.Control name="telefono" value={formData.telefono} onChange={handleInputChange} />
              </Col>
              <Col md={12} className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={12} className="mb-3">
                <Form.Label>Dirección</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                />
              </Col>
              {editing && (
                <Col md={12} className="mb-2">
                  <Form.Check
                    type="switch"
                    id="activo-cf"
                    name="activo"
                    label="Activo"
                    checked={formData.activo}
                    onChange={handleInputChange}
                  />
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ClientesFacturacion;
